// ── Sigma.js WebGL Graph Scene ──
// GPU-accelerated 2D graph visualization using Sigma.js (WebGL2 shaders).
// ForceAtlas2 layout runs in a Web Worker for non-blocking positioning.

import { useRef, useEffect, useCallback, useState } from "react";
import { MultiGraph } from "graphology";
import Sigma from "sigma";
import FA2Layout from "graphology-layout-forceatlas2/worker";

import type { GraphNode, NodeType } from "../types/graph";
import { nodeColor } from "../utils/colors";
import { dedupeGraphLinks, dedupeGraphNodes } from "../hooks/nodeExpandUtils";

// ── Edge filter categories ──
type EdgeFilter = "all" | "none" | "structural" | "semantic";

const STRUCTURAL_TYPES = new Set([
  "has-campaign", "HAS_CAMPAIGN",
  "has-area", "CONTAINS_AREA",
  "has-element",
  "has-inspection", "HAS_INSPECTION",
]);
const SEMANTIC_TYPES = new Set([
  "SAME_DEFECT", "SAME_ASSET", "SAME_AREA", "SAME_PERIOD",
  "NEAR", "PRECEDED_BY",
]);

function edgePassesFilter(type: string, filter: EdgeFilter): boolean {
  if (filter === "all") return true;
  if (filter === "none") return false;
  if (filter === "structural") return STRUCTURAL_TYPES.has(type);
  if (filter === "semantic") return SEMANTIC_TYPES.has(type);
  return true;
}

// ── Props contract (unchanged) ──

interface GraphSceneProps {
  nodes: GraphNode[];
  links: Array<{ source: string; target: string; type: string }>;
  onNodeClick: (node: GraphNode) => void;
  width: number;
  height: number;
  onExpandAll?: () => void;
  onRetractOneLevel?: () => void;
  onExpandSelected?: (() => void) | undefined;
}

// ── Node sizing by hierarchy level ──

const NODE_SIZE: Record<NodeType, number> = {
  site: 26,
  campaign: 18,
  area: 12,
  element: 8,
  inspection: 5,
};

// ── Compute importance/weight for a link type ──
// Maps link types to default importance levels (1-10).
// Can be overridden by explicit weight field on GraphLink.
function getImportanceForType(type: string): number {
  if (type === "SAME_DEFECT") return 9; // Critical: suggests systemic issue
  if (type === "SAME_ASSET") return 8; // High: same asset appears in multiple places
  if (type === "HAS_CAMPAIGN" || type === "has-campaign") return 10; // Structural: site→campaign hierarchy
  if (type === "CONTAINS_AREA" || type === "has-area") return 10; // Structural: campaign→area hierarchy
  if (type === "HAS_INSPECTION" || type === "has-inspection") return 7; // Medium-high: area→inspection
  if (type === "SAME_AREA") return 6; // Medium: same area in different periods
  if (type === "NEAR") return 5; // Medium: spatial proximity
  if (type === "SAME_PERIOD") return 4; // Low-medium: temporal correlation
  if (type === "has-element") return 7; // Medium-high: campaign→element
  if (type.startsWith("xlink-")) return 1; // Very low: cross-links
  return 3; // Default low importance
}

// ── Edge colour by relationship type ──
// Dark, opaque colours visible on light/white backgrounds.
function edgeColorFn(type: string): string {
  if (type.startsWith("xlink-")) return "rgba(120, 120, 120, 0.25)";
  if (type === "has-campaign" || type === "HAS_CAMPAIGN") return "#1f3a32";       // Evergreen
  if (type === "has-area" || type === "CONTAINS_AREA") return "#2a5a4a";          // Forest
  if (type === "has-element") return "#4c615b";                                    // Evergreen 80%
  if (type === "has-inspection" || type === "HAS_INSPECTION") return "#556b65";   // Dark grey-green
  if (type === "NEAR") return "#b8860b";                                           // Dark goldenrod
  if (type === "SAME_DEFECT") return "#b91c1c";                                   // Dark red
  if (type === "SAME_ASSET") return "#1e3a8a";                                    // Deep blue
  if (type === "SAME_AREA") return "#6b21a8";                                     // Deep purple
  if (type === "SAME_PERIOD") return "#1e40af";                                   // Dark blue
  if (type === "PRECEDED_BY") return "#111827";                                   // Near-black
  return "rgba(80, 80, 80, 0.5)";                                                 // Default dark grey
}

// ── Edge size by type and importance weight ──
// Higher importance = thicker edge.
function edgeSizeFn(type: string, weight?: number): number {
  const importance = weight !== undefined ? weight : getImportanceForType(type);
  // Normalize importance to size multiplier (0.5 to 2.0)
  const sizeBoost = 0.5 + (Math.min(importance, 10) / 10) * 1.5;
  
  if (type.startsWith("xlink-")) return 0.15 * sizeBoost;
  if (type === "has-campaign" || type === "HAS_CAMPAIGN") return 1.2 * sizeBoost;
  if (type === "has-area" || type === "CONTAINS_AREA") return 0.9 * sizeBoost;
  if (type === "has-element") return 0.6 * sizeBoost;
  if (type === "NEAR") return 0.35 * sizeBoost;
  if (type === "SAME_DEFECT") return 0.5 * sizeBoost;
  if (type === "SAME_ASSET") return 0.45 * sizeBoost;
  if (type === "SAME_AREA") return 0.45 * sizeBoost;
  if (type === "SAME_PERIOD") return 0.35 * sizeBoost;
  return 0.45 * sizeBoost;
}

// ── Control button ──

function ControlButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: "8px 14px",
        borderRadius: 6,
        border: "1px solid rgba(31, 58, 50, 0.2)",
        background: "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(8px)",
        color: "#1f3a32",
        fontSize: 12,
        fontFamily: "'Lexend', system-ui, sans-serif",
        cursor: "pointer",
        letterSpacing: "0.5px",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(31, 58, 50, 0.5)";
        e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(31, 58, 50, 0.2)";
        e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
      }}
    >
      {children}
    </button>
  );
}

// ── Main Export ──

export default function GraphScene({
  nodes,
  links,
  onNodeClick,
  width,
  height,
  onExpandAll,
  onRetractOneLevel,
  onExpandSelected,
}: GraphSceneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sigmaRef = useRef<Sigma | null>(null);
  const graphRef = useRef(new MultiGraph());
  const layoutRef = useRef<FA2Layout | null>(null);
  const layoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;

  // Hover state for neighbour highlighting
  const hoveredRef = useRef<string | null>(null);
  const neighborSetRef = useRef<Set<string> | null>(null);

  // Edge filter state
  const [edgeFilter, setEdgeFilter] = useState<EdgeFilter>("all");
  const edgeFilterRef = useRef<EdgeFilter>("all");
  edgeFilterRef.current = edgeFilter;

  // ── Sync React state → graphology model ──
  useEffect(() => {
    const graph = graphRef.current;
    const uniqueNodes = dedupeGraphNodes(nodes);
    const uniqueLinks = dedupeGraphLinks(links);
    const prevCount = graph.order;

    // Save existing positions so nodes don't jump
    const positions = new Map<string, { x: number; y: number }>();
    graph.forEachNode((id, attrs) => {
      positions.set(id, { x: attrs.x as number, y: attrs.y as number });
    });

    graph.clear();

    for (const node of uniqueNodes) {
      const saved = positions.get(node.id);
      if (graph.hasNode(node.id)) continue;
      graph.addNode(node.id, {
        x: saved?.x ?? (Math.random() - 0.5) * 200,
        y: saved?.y ?? (Math.random() - 0.5) * 200,
        size: NODE_SIZE[node.type] ?? 3.5,
        color: nodeColor(node.type, node.severity),
        label: node.label,
        nodeType: node.type,
        severity: node.severity,
        childCount: node.childCount,
        expanded: node.expanded,
        meta: node.meta,
      });
    }

    for (const link of uniqueLinks) {
      const key = `${link.source}\u2192${link.target}\u2192${link.type}`;
      if (!graph.hasNode(link.source) || !graph.hasNode(link.target)) continue;
      if (!graph.hasEdge(key)) {
        graph.addEdgeWithKey(key, link.source, link.target, {
          color: edgeColorFn(link.type),
          size: edgeSizeFn(link.type, link.weight),
          linkType: link.type,
        });
      }
    }

    sigmaRef.current?.refresh();

    // (Re)start layout when graph topology changes
    if (uniqueNodes.length > 0 && uniqueNodes.length !== prevCount) {
      if (layoutRef.current) {
        layoutRef.current.kill();
        layoutRef.current = null;
      }
      if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current);

      const layout = new FA2Layout(graph, {
        settings: {
          gravity: 1.5,
          scalingRatio: 4,
          barnesHutOptimize: true,
          barnesHutTheta: 0.5,
          slowDown: 3,
          strongGravityMode: true,
        },
      });
      layout.start();
      layoutRef.current = layout;

      // Auto-stop after convergence
      layoutTimerRef.current = setTimeout(
        () => layout.stop(),
        Math.min(5000, 1500 + uniqueNodes.length * 2),
      );
    }
  }, [nodes, links]);

  // ── Initialise Sigma renderer (once on mount) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const sigma = new Sigma(graphRef.current, el, {
      renderEdgeLabels: false,
      labelRenderedSizeThreshold: 4,
      labelFont: "'Lexend', system-ui, sans-serif",
      labelColor: { color: "#1f3a32" },
      labelSize: 22,
      defaultEdgeType: "line",
      zIndex: true,
      minCameraRatio: 0.02,
      maxCameraRatio: 15,
      nodeReducer: (node, data) => {
        const h = hoveredRef.current;
        if (!h) return data;
        if (node === h) return { ...data, highlighted: true, zIndex: 1 };
        if (neighborSetRef.current?.has(node)) return data;
        return { ...data, color: "#d2d8d6", label: null, zIndex: 0 };
      },
      edgeReducer: (edge, data) => {
        const g = graphRef.current;
        const linkType = g.getEdgeAttribute(edge, "linkType") as string;
        const f = edgeFilterRef.current;

        // Apply edge type filter
        if (!edgePassesFilter(linkType, f)) {
          return { ...data, hidden: true };
        }

        const h = hoveredRef.current;
        if (!h) return data;
        if (g.source(edge) === h || g.target(edge) === h) {
          return { ...data, size: Math.max(data.size as number, 2), zIndex: 1 };
        }
        return { ...data, hidden: true };
      },
    });

    // Click handler
    sigma.on("clickNode", ({ node }) => {
      const a = graphRef.current.getNodeAttributes(node);
      onNodeClickRef.current({
        id: node,
        type: a.nodeType as NodeType,
        label: a.label as string,
        severity: a.severity as number | undefined,
        childCount: a.childCount as number | undefined,
        expanded: a.expanded as boolean | undefined,
        meta: a.meta as GraphNode["meta"],
      });
    });

    // Hover → highlight neighbours
    sigma.on("enterNode", ({ node }) => {
      hoveredRef.current = node;
      neighborSetRef.current = new Set(graphRef.current.neighbors(node));
      sigma.refresh();
    });
    sigma.on("leaveNode", () => {
      hoveredRef.current = null;
      neighborSetRef.current = null;
      sigma.refresh();
    });

    sigmaRef.current = sigma;

    return () => {
      if (layoutRef.current) {
        layoutRef.current.kill();
        layoutRef.current = null;
      }
      if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current);
      sigma.kill();
      sigmaRef.current = null;
    };
  }, []);

  // ── Resize ──
  useEffect(() => {
    sigmaRef.current?.refresh();
  }, [width, height]);

  // ── Refresh when edge filter changes ──
  useEffect(() => {
    sigmaRef.current?.refresh();
  }, [edgeFilter]);

  // ── Camera reset when node count changes ──
  const prevCountRef = useRef(0);
  useEffect(() => {
    if (nodes.length > 0 && nodes.length !== prevCountRef.current) {
      prevCountRef.current = nodes.length;
      setTimeout(
        () => sigmaRef.current?.getCamera().animatedReset({ duration: 500 }),
        800,
      );
    }
  }, [nodes.length]);

  const handleZoomFit = useCallback(() => {
    sigmaRef.current?.getCamera().animatedReset({ duration: 400 });
  }, []);

  return (
    <div style={{ width, height, position: "absolute", top: 0, left: 0 }}>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          background: "#fbf9f0",
        }}
      />

      {/* Control buttons */}
      <div
        style={{
          position: "absolute",
          bottom: 48,
          right: 16,
          zIndex: 30,
          display: "flex",
          gap: 6,
          flexDirection: "row",
        }}
      >
        {onExpandAll && (
          <ControlButton onClick={onExpandAll} title="Expand all nodes to all levels">
            ⊕ EXPAND ALL
          </ControlButton>
        )}
        {onRetractOneLevel && (
          <ControlButton onClick={onRetractOneLevel} title="Collapse deepest level">
            ⊖ RETRACT
          </ControlButton>
        )}
        {onExpandSelected && (
          <ControlButton onClick={onExpandSelected} title="Expand selected node">
            ◎ EXPAND NODE
          </ControlButton>
        )}
        <ControlButton onClick={handleZoomFit} title="Zoom to fit all nodes">
          ⟳ FIT VIEW
        </ControlButton>
      </div>

      {/* Edge filter controls */}
      <div
        style={{
          position: "absolute",
          bottom: 48,
          left: 16,
          zIndex: 30,
          display: "flex",
          gap: 4,
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontFamily: "'Lexend', system-ui, sans-serif",
            color: "#1f3a32",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            marginRight: 4,
            opacity: 0.7,
          }}
        >
          Edges
        </span>
        {(["all", "structural", "semantic", "none"] as EdgeFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setEdgeFilter(f)}
            title={
              f === "all" ? "Show all edges" :
              f === "none" ? "Hide all edges" :
              f === "structural" ? "Show structural edges (hierarchy)" :
              "Show semantic edges (relationships)"
            }
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              border: edgeFilter === f
                ? "1.5px solid #1f3a32"
                : "1px solid rgba(31, 58, 50, 0.2)",
              background: edgeFilter === f
                ? "rgba(31, 58, 50, 0.12)"
                : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(8px)",
              color: "#1f3a32",
              fontSize: 11,
              fontFamily: "'Lexend', system-ui, sans-serif",
              cursor: "pointer",
              letterSpacing: "0.3px",
              fontWeight: edgeFilter === f ? 600 : 400,
              transition: "all 0.15s",
              whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}
          >
            {f === "all" ? "ALL" : f === "none" ? "HIDE" : f === "structural" ? "STRUCT" : "SEMANTIC"}
          </button>
        ))}
      </div>
    </div>
  );
}
